/**
 * SSO 单点登录工具
 *
 * 支持两种跳转模式，通过 config.toolbox.ssoMode 配置：
 *
 * - "shared"（默认）：同域共享 cookie，直接跳转。
 *   适用于 portal 与 toolbox 同属一个顶级域（如 .oran.cn）的场景，
 *   登录 token 通过 cookie（domain=.oran.cn）自动共享，无需额外认证。
 *
 * - "oauth2"：走 OAuth2 Authorization Code 流程。
 *   适用于跨顶级域（如 toolbox.photog.art）的场景，
 *   cookie 无法跨域共享，需要先获取 authorization code 再携带跳转。
 *   流程：
 *   1. 前端调用 GET /oauth2/authorize?clientId=portal-a（携带 Bearer token）
 *   2. 后端验证通过后返回 authorization code
 *   3. 前端携带 code 跳转到 toolbox 首页（?oauth_code=xxx）
 *   4. toolbox 后端用 code 调用 POST /oauth2/token 换取 access token，完成 SSO 登录
 */

import { oauth2Authorize } from '@/lib/api/auth';
import { config } from '@/lib/config';
import { getToken } from './auth-storage';
import { logger } from '@/lib/logger';

/**
 * 跳转到 toolbox
 *
 * 根据 config.toolbox.ssoMode 选择跳转方式：
 * - "shared"  → 同域 cookie 共享，同步直接跳转
 * - "oauth2"  → 先获取 authorization code，再携带 code 异步跳转
 *
 * @throws 如果未登录（两种模式都会抛出 NOT_AUTHENTICATED）
 */
export async function navigateToToolbox(): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error('NOT_AUTHENTICATED');
  }

  const mode = config.toolbox.ssoMode;

  if (mode === 'oauth2') {
    try {
      const res = await oauth2Authorize();

      if (res.success && res.data?.code) {
        // 拿到 authorization code，以 oauth_code 查询参数拼接到 toolbox 首页地址
        const toolboxCallbackUrl = `${config.toolbox.baseUrl}?oauth_code=${encodeURIComponent(res.data.code)}`;
        logger.info('SSO (oauth2): redirecting to toolbox with oauth_code');
        window.location.href = toolboxCallbackUrl;
      } else {
        // 后端未返回 code（可能用户未授权或会话过期），降级为直接跳转
        logger.warn('SSO (oauth2): oauth2/authorize did not return code, falling back to direct navigation');
        window.location.href = config.toolbox.baseUrl;
      }
    } catch (error) {
      // 接口调用失败，降级为直接跳转
      logger.error('SSO (oauth2): oauth2/authorize failed, falling back to direct navigation', error as Error);
      window.location.href = config.toolbox.baseUrl;
    }
  } else {
    // shared 模式：同域 cookie 共享，直接跳转
    logger.info('SSO (shared): redirecting to toolbox via cookie sharing');
    window.location.href = config.toolbox.baseUrl;
  }
}

/**
 * 判断当前 SSO 模式是否为异步（oauth2）
 * 调用方据此决定是否需要 loading 状态
 */
export function isSSOAsync(): boolean {
  return config.toolbox.ssoMode === 'oauth2';
}
